import { computed, inject, Injectable, signal } from "@angular/core";

import { HttpClient } from "@angular/common/http";

import { Observable, Subject, tap } from "rxjs";

import {
    AuthResponseDto,
    AuthRole,
    AdminLoginRequestDto,
    UserOtpRequestDto,
    UserOtpResendRequestDto,
    UserOtpVerifyRequestDto,
    UserOtpResponseDto,
    AdminForgotPasswordRequestDto,
    AdminEmailExistRequestDto,
    AdminEmailExistResponseDto,
    AdminResetPasswordResponseDto,
} from "../../../../models/auth.dto";

import { environment } from "~/src/environments/environment";
import { AuthSession } from "..";

@Injectable({
    providedIn: "root",
})
export class AuthService {
    private static readonly USER_SESSION_STORAGE_KEY = "app.auth.user.session";

    private static readonly ADMIN_SESSION_STORAGE_KEY = "app.auth.admin.session";

    private static readonly MAX_TIMEOUT_DELAY = 2_147_483_647;

    private readonly http = inject(HttpClient);

    private readonly userSessionState = signal<AuthSession | null>(null);

    private readonly adminSessionState = signal<AuthSession | null>(null);

    private readonly sessionExpiredSubject = new Subject<AuthRole>();

    private userExpiryTimer: ReturnType<typeof setTimeout> | null = null;

    private adminExpiryTimer: ReturnType<typeof setTimeout> | null = null;

    public readonly userSession = this.userSessionState.asReadonly();

    public readonly adminSession = this.adminSessionState.asReadonly();

    public readonly isUserAuthenticated = computed(() => this.isSessionValid("user"));

    public readonly userAccessToken = computed(() => this.userSessionState()?.accessToken ?? null);

    public readonly userMobileNumber = computed(() => this.userSessionState()?.mobileNumber ?? null);

    public readonly userRole = computed<AuthRole | null>(() => this.userSessionState()?.role ?? null);

    public readonly userExpiresAt = computed(() => this.userSessionState()?.expiresAt ?? null);

    public readonly isAdminAuthenticated = computed(() => this.isSessionValid("admin"));

    public readonly adminAccessToken = computed(() => this.adminSessionState()?.accessToken ?? null);

    public readonly adminMobileNumber = computed(() => this.adminSessionState()?.mobileNumber ?? null);

    public readonly adminRole = computed<AuthRole | null>(() => this.adminSessionState()?.role ?? null);

    public readonly adminExpiresAt = computed(() => this.adminSessionState()?.expiresAt ?? null);

    public readonly sessionExpired$ = this.sessionExpiredSubject.asObservable();

    constructor() {
        this.restoreSession("user");
        this.restoreSession("admin");
    }

    public requestUserOtp(request: UserOtpRequestDto): Observable<UserOtpResponseDto> {
        return this.http.post<UserOtpResponseDto>(`${environment.apiUrlBasePath}/auth/user/request-otp`, request);
    }

    public resendUserOtp(request: UserOtpResendRequestDto): Observable<UserOtpResponseDto> {
        return this.http.post<UserOtpResponseDto>(`${environment.apiUrlBasePath}/auth/user/resend-otp`, request);
    }

    public verifyUserOtp(request: UserOtpVerifyRequestDto): Observable<AuthResponseDto> {
        return this.http.post<AuthResponseDto>(`${environment.apiUrlBasePath}/auth/user/verify-otp`, request).pipe(
            tap((response) => {
                this.startSession("user", response);
            }),
        );
    }

    public logoutUser(): void {
        this.clearSession("user");
    }

    public adminLogin(request: AdminLoginRequestDto): Observable<AuthResponseDto> {
        return this.http.post<AuthResponseDto>(`${environment.apiUrlBasePath}/auth/admin/login`, request).pipe(
            tap((response) => {
                this.startSession("admin", response);
            }),
        );
    }

    public logoutAdmin(): void {
        this.clearSession("admin");
    }

    public getAccessToken(role: AuthRole): string | null {
        return this.getSession(role)?.accessToken ?? null;
    }

    public hasValidSession(role: AuthRole): boolean {
        return this.isSessionValid(role);
    }

    public hasRole(role: AuthRole): boolean {
        return this.hasValidSession(role);
    }

    public logout(role: AuthRole): void {
        this.clearSession(role);
    }

    public logoutAll(): void {
        this.clearSession("user");
        this.clearSession("admin");
    }

    private startSession(role: AuthRole, response: AuthResponseDto): void {
        if (!this.isSuccessfulAuthResponse(response)) {
            return;
        }

        if (response.role !== role) {
            return;
        }

        const session: AuthSession = {
            accessToken: response.token,
            expiresAt: response.expiresAt,
            mobileNumber: response.mobileNumber ?? null,
            email: response.email ?? null,
            role,
        };

        this.setSession(role, session);
        this.scheduleAutomaticLogout(role, session.expiresAt);
    }

    private restoreSession(role: AuthRole): void {
        const session = this.readStoredSession(role);

        if (!session) {
            return;
        }

        if (!this.isValidStoredSession(session, role)) {
            this.clearStoredSession(role);
            return;
        }

        if (session.expiresAt <= Date.now()) {
            this.clearStoredSession(role);
            return;
        }

        this.setSession(role, session);
        this.scheduleAutomaticLogout(role, session.expiresAt);
    }

    private setSession(role: AuthRole, session: AuthSession): void {
        this.persistSession(role, session);
        this.getSessionState(role).set(session);
    }

    private clearSession(role: AuthRole): void {
        this.clearExpiryTimer(role);
        this.clearStoredSession(role);
        this.getSessionState(role).set(null);
    }

    private handleSessionExpiration(role: AuthRole): void {
        this.clearSession(role);
        this.sessionExpiredSubject.next(role);
    }

    private scheduleAutomaticLogout(role: AuthRole, expiresAt: number): void {
        this.clearExpiryTimer(role);

        const remainingTime = expiresAt - Date.now();

        if (remainingTime <= 0) {
            this.handleSessionExpiration(role);
            return;
        }

        const delay = Math.min(remainingTime, AuthService.MAX_TIMEOUT_DELAY);

        this.setExpiryTimer(
            role,
            setTimeout(() => {
                const session = this.getSession(role);

                if (!session) {
                    return;
                }

                if (session.expiresAt <= Date.now()) {
                    this.handleSessionExpiration(role);
                    return;
                }

                this.scheduleAutomaticLogout(role, session.expiresAt);
            }, delay),
        );
    }

    private getSession(role: AuthRole): AuthSession | null {
        return this.getSessionState(role)();
    }

    private getSessionState(role: AuthRole) {
        return role === "user" ? this.userSessionState : this.adminSessionState;
    }

    private getStorageKey(role: AuthRole): string {
        return role === "user" ? AuthService.USER_SESSION_STORAGE_KEY : AuthService.ADMIN_SESSION_STORAGE_KEY;
    }

    private getExpiryTimer(role: AuthRole): ReturnType<typeof setTimeout> | null {
        return role === "user" ? this.userExpiryTimer : this.adminExpiryTimer;
    }

    private setExpiryTimer(role: AuthRole, timer: ReturnType<typeof setTimeout>): void {
        if (role === "user") {
            this.userExpiryTimer = timer;
            return;
        }

        this.adminExpiryTimer = timer;
    }

    private clearExpiryTimer(role: AuthRole): void {
        const timer = this.getExpiryTimer(role);

        if (!timer) {
            return;
        }

        clearTimeout(timer);

        if (role === "user") {
            this.userExpiryTimer = null;
            return;
        }

        this.adminExpiryTimer = null;
    }

    private persistSession(role: AuthRole, session: AuthSession): void {
        localStorage.setItem(this.getStorageKey(role), JSON.stringify(session));
    }

    private readStoredSession(role: AuthRole): AuthSession | null {
        const storedSession = localStorage.getItem(this.getStorageKey(role));

        if (!storedSession) {
            return null;
        }

        try {
            return JSON.parse(storedSession) as AuthSession;
        } catch {
            this.clearStoredSession(role);
            return null;
        }
    }

    private clearStoredSession(role: AuthRole): void {
        localStorage.removeItem(this.getStorageKey(role));
    }

    private isSessionValid(role: AuthRole): boolean {
        const session = this.getSession(role);

        return session !== null && session.expiresAt > Date.now();
    }

    private isSuccessfulAuthResponse(response: AuthResponseDto): response is AuthResponseDto & {
        token: string;
        expiresAt: number;
        role: AuthRole;
    } {
        return (
            response.success === true &&
            typeof response.token === "string" &&
            response.token.length > 0 &&
            typeof response.expiresAt === "number" &&
            response.expiresAt > 0 &&
            (response.role === "user" || response.role === "admin")
        );
    }

    private isValidStoredSession(session: AuthSession, role: AuthRole): boolean {
        return (
            typeof session === "object" &&
            session !== null &&
            typeof session.accessToken === "string" &&
            session.accessToken.length > 0 &&
            typeof session.expiresAt === "number" &&
            session.expiresAt > 0 &&
            session.role === role &&
            (session.mobileNumber === null || typeof session.mobileNumber === "string")
        );
    }

    public isAdminEmailExist(request: AdminEmailExistRequestDto): Observable<AdminEmailExistResponseDto> {
        const email = request.email.trim().toLowerCase();

        return this.http.post<AdminEmailExistResponseDto>(`${environment.apiUrlBasePath}/auth/admin/email-exist`, {
            email,
        });
    }

    public requestAdminPasswordReset(request: AdminForgotPasswordRequestDto): Observable<string> {
        return this.http.post<string>(`${environment.apiUrlBasePath}/auth/admin/forgot-password`, request);
    }

    public resetAdminPassword(request: { uuid: string; password: string }): Observable<AdminResetPasswordResponseDto> {
        return this.http.post<AdminResetPasswordResponseDto>(
            `${environment.apiUrlBasePath}/auth/admin/reset-password`,
            request,
        );
    }
}
